import React, { useState, useEffect } from 'react';
import { Building, Plus, Trash2, Pencil, Check, X, FileText, History, Send, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../ui/table";
import { confirmDialog } from "../ui/confirm-dialog";

const AcquiredAssets = ({ axiosInstance }) => {
    const [branches, setBranches] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [assets, setAssets] = useState([]);
    const [categories, setCategories] = useState([]);
    const [isAddBranchOpen, setIsAddBranchOpen] = useState(false);
    const [isEditBranchOpen, setIsEditBranchOpen] = useState(false);
    const [isDeleteBranchOpen, setIsDeleteBranchOpen] = useState(false);
    const [branchToEdit, setBranchToEdit] = useState(null);
    const [branchToDelete, setBranchToDelete] = useState(null);
    
    // Branch Form Data
    const [formData, setFormData] = useState({
        name: '',
        person_in_charge: '',
        contact_number: ''
    });

    // Category Management State
    const [isAddCategoryModalOpen, setIsAddCategoryModalOpen] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [isEditCategoryModalOpen, setIsEditCategoryModalOpen] = useState(false);
    const [categoryToEdit, setCategoryToEdit] = useState(null);
    const [editCategoryName, setEditCategoryName] = useState('');
    const [isDeleteCategoryModalOpen, setIsDeleteCategoryModalOpen] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState(null);

    // Asset Management State
    const [editingAssetId, setEditingAssetId] = useState(null);
    const [editFormData, setEditFormData] = useState({});
    const [isAddingItem, setIsAddingItem] = useState(false);
    const [activeCategoryTab, setActiveCategoryTab] = useState(null);
    const [newAssetData, setNewAssetData] = useState({
        item: '',
        description: '',
        inclusion: '',
        date_acquired: '',
        awarded_to: '',
        award_date: '',
        status: 'Functional',
        details: ''
    });

    // Details Modal State
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [detailsContext, setDetailsContext] = useState(null); // { type: 'new' | 'edit', assetId?: string, status: string, details: string }

    // History Modal State
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const [selectedAssetHistory, setSelectedAssetHistory] = useState([]);
    const [selectedAssetItemName, setSelectedAssetItemName] = useState("");

    // Send Asset State
    const [isSendAssetModalOpen, setIsSendAssetModalOpen] = useState(false);
    const [assetToSend, setAssetToSend] = useState(null);
    const [targetBranchId, setTargetBranchId] = useState('');

    // Filtering & Pagination State
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;

    useEffect(() => {
        fetchBranches();
    }, []);

    useEffect(() => {
        if (selectedBranch) {
            fetchCategories(selectedBranch.id);
            // Reset states
            setIsAddCategoryModalOpen(false);
            setIsAddingItem(false);
            setEditingAssetId(null);
            setNewAssetData({ item: '', description: '', inclusion: '', date_acquired: '', awarded_to: '', award_date: '', status: 'Functional', details: '' });
        }
    }, [selectedBranch]);

    useEffect(() => {
        if (selectedBranch && activeCategoryTab) {
            fetchAssets(selectedBranch.id, activeCategoryTab);
            setIsAddingItem(false);
            setEditingAssetId(null);
            setSearchQuery('');
            setCurrentPage(1);
        }
    }, [activeCategoryTab, selectedBranch]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery]);

    const fetchBranches = async () => {
        try {
            const response = await axiosInstance.get('/inventory/branches');
            setBranches(response.data);
        } catch (error) {
            console.error('Error fetching branches:', error);
            toast.error('Failed to fetch branches');
        }
    };

    const fetchCategories = async (branchId) => {
        try {
            const response = await axiosInstance.get(`/inventory/assets/categories?branch_id=${branchId}`);
            setCategories(response.data);
            if (response.data.length > 0 && !activeCategoryTab) {
                setActiveCategoryTab(response.data[0].name);
            } else if (response.data.length === 0) {
                setActiveCategoryTab(null);
                setAssets([]);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
            toast.error('Failed to fetch categories');
        }
    };

    const fetchAssets = async (branchId, categoryName) => {
        if (!categoryName) return;
        try {
            const response = await axiosInstance.get(`/inventory/assets/items?branch_id=${branchId}&category=${encodeURIComponent(categoryName)}`);
            setAssets(response.data);
        } catch (error) {
            console.error('Error fetching assets:', error);
            toast.error('Failed to fetch assets');
        }
    };

    const getBranchName = (branchId) => {
        const branch = branches.find(b => b.id.toString() === branchId?.toString());
        return branch ? branch.name : branchId;
    };

    const handleCreateBranch = async (e) => {
        e.preventDefault();
        try {
            await axiosInstance.post('/inventory/branches', formData);
            toast.success('Branch office created successfully');
            setIsAddBranchOpen(false);
            setFormData({ name: '', person_in_charge: '', contact_number: '' });
            fetchBranches();
        } catch (error) {
            console.error('Error creating branch:', error);
            toast.error('Failed to create branch office');
        }
    };

    const handleEditBranch = async (e) => {
        e.preventDefault();
        try {
            await axiosInstance.put(`/inventory/branches/${branchToEdit.id}`, formData);
            toast.success('Branch office updated successfully');
            setIsEditBranchOpen(false);
            setBranchToEdit(null);
            setFormData({ name: '', person_in_charge: '', contact_number: '' });
            fetchBranches();
            // Update selected branch if it's the one being edited
            if (selectedBranch?.id === branchToEdit.id) {
                setSelectedBranch({ ...selectedBranch, ...formData });
            }
        } catch (error) {
            console.error('Error updating branch:', error);
            toast.error('Failed to update branch office');
        }
    };

    const handleDeleteBranch = async () => {
        if (!branchToDelete) return;
        try {
            await axiosInstance.delete(`/inventory/branches/${branchToDelete.id}`);
            toast.success('Branch office deleted successfully');
            setIsDeleteBranchOpen(false);
            setBranchToDelete(null);
            fetchBranches();
            if (selectedBranch?.id === branchToDelete.id) {
                setSelectedBranch(null);
            }
        } catch (error) {
            console.error('Error deleting branch:', error);
            toast.error('Failed to delete branch office');
        }
    };

    const openEditBranch = (branch, e) => {
        e.stopPropagation();
        setBranchToEdit(branch);
        setFormData({
            name: branch.name,
            person_in_charge: branch.person_in_charge,
            contact_number: branch.contact_number
        });
        setIsEditBranchOpen(true);
    };

    const openDeleteBranch = (branch, e) => {
        e.stopPropagation();
        setBranchToDelete(branch);
        setIsDeleteBranchOpen(true);
    };

    // Category Management Functions
    const handleAddCategoryClick = () => {
        setIsAddCategoryModalOpen(true);
    };

    const handleSaveNewCategory = async () => {
        if (!newCategoryName.trim()) {
            toast.error("Please enter a category name");
            return;
        }

        try {
            const payload = {
                branch_id: selectedBranch.id,
                name: newCategoryName.trim()
            };
            await axiosInstance.post('/inventory/assets/categories', payload);
            toast.success("Category added successfully");
            setIsAddCategoryModalOpen(false);
            setNewCategoryName('');
            fetchCategories(selectedBranch.id);
            setActiveCategoryTab(newCategoryName.trim());
        } catch (error) {
            console.error("Error creating category:", error);
            toast.error("Failed to create category");
        }
    };

    const handleEditCategoryClick = (category) => {
        setCategoryToEdit(category);
        setEditCategoryName(category.name);
        setIsEditCategoryModalOpen(true);
    };

    const handleSaveEditCategory = async () => {
        if (!editCategoryName.trim()) {
            toast.error("Please enter a category name");
            return;
        }

        try {
            const payload = {
                branch_id: selectedBranch.id,
                name: editCategoryName.trim()
            };
            await axiosInstance.put(`/inventory/assets/categories/${categoryToEdit.id}`, payload);
            toast.success("Category updated successfully");
            setIsEditCategoryModalOpen(false);
            setCategoryToEdit(null);
            fetchCategories(selectedBranch.id);
            if (activeCategoryTab === categoryToEdit.name) {
                setActiveCategoryTab(editCategoryName.trim());
            }
        } catch (error) {
            console.error("Error updating category:", error);
            toast.error(error.response?.data?.detail || "Failed to update category");
        }
    };

    const handleDeleteCategoryClick = (category) => {
        setCategoryToDelete(category);
        setIsDeleteCategoryModalOpen(true);
    };

    const handleConfirmDeleteCategory = async () => {
        if (!categoryToDelete) return;
        try {
            await axiosInstance.delete(`/inventory/assets/categories/${categoryToDelete.id}`);
            toast.success("Category deleted successfully");
            setIsDeleteCategoryModalOpen(false);
            setCategoryToDelete(null);
            if (activeCategoryTab === categoryToDelete.name) {
                setActiveCategoryTab(null);
                setAssets([]);
            }
            fetchCategories(selectedBranch.id);
        } catch (error) {
            console.error("Error deleting category:", error);
            toast.error("Failed to delete category");
        }
    };

    // Asset Management Functions
    const handleAddItemClick = () => {
        setIsAddingItem(true);
    };

    const handleSaveNewItem = async () => {
        if (!newAssetData.item || !newAssetData.date_acquired) {
            toast.error("Please fill in required fields (Item, Date Acquired)");
            return;
        }

        try {
            const payload = {
                ...newAssetData,
                branch_id: selectedBranch.id,
                category: activeCategoryTab
            };
            await axiosInstance.post('/inventory/assets/items', payload);
            toast.success("Item added successfully");
            setIsAddingItem(false);
            setNewAssetData({ item: '', description: '', inclusion: '', date_acquired: '', awarded_to: '', award_date: '', status: 'Functional', details: '' });
            fetchAssets(selectedBranch.id, activeCategoryTab);
        } catch (error) {
            console.error("Error creating item:", error);
            toast.error("Failed to create item");
        }
    };

    const handleCancelAddItem = () => {
        setIsAddingItem(false);
        setNewAssetData({ item: '', description: '', inclusion: '', date_acquired: '', awarded_to: '', award_date: '', status: 'Functional', details: '' });
    };

    const handleEditClick = (asset) => {
        setEditingAssetId(asset.id);
        setEditFormData({ ...asset });
    };

    const handleHistoryClick = (asset) => {
        setSelectedAssetHistory(asset.history || []);
        setSelectedAssetItemName(asset.item || "");
        setIsHistoryModalOpen(true);
    };

    const handleCancelEdit = () => {
        setEditingAssetId(null);
        setEditFormData({});
    };

    const handleSaveEdit = async () => {
        try {
            await axiosInstance.put(`/inventory/assets/items/${editingAssetId}`, editFormData);
            toast.success("Item updated successfully");
            setEditingAssetId(null);
            setEditFormData({});
            fetchAssets(selectedBranch.id, activeCategoryTab);
        } catch (error) {
            console.error("Error updating item:", error);
            toast.error("Failed to update item");
        }
    };

    const handleDeleteAsset = async (assetId, assetBranchId) => {
        const confirmed = await confirmDialog({
            title: "Delete Item",
            description: "Are you sure you want to delete this item? This action cannot be undone.",
            confirmText: "Delete",
            variant: "destructive"
        });
        if (!confirmed) return;
        
        try {
            await axiosInstance.delete(`/inventory/assets/items/${assetId}?branch_id=${assetBranchId}&category=${encodeURIComponent(activeCategoryTab)}`);
            toast.success("Item deleted successfully");
            fetchAssets(selectedBranch.id, activeCategoryTab);
        } catch (error) {
            console.error("Error deleting item:", error);
            toast.error("Failed to delete item");
        }
    };

    // Send Asset Logic
    const handleSendClick = (asset) => {
        setAssetToSend(asset);
        setTargetBranchId('');
        setIsSendAssetModalOpen(true);
    };

    const handleSendAsset = async () => {
        if (!targetBranchId) {
            toast.error("Please select a target branch");
            return;
        }

        try {
            await axiosInstance.post(`/inventory/assets/items/${assetToSend.id}/transfer`, {
                source_branch_id: assetToSend.branch_id.toString(),
                target_branch_id: targetBranchId.toString(),
                category: activeCategoryTab
            });
            toast.success("Item sent successfully");
            setIsSendAssetModalOpen(false);
            setAssetToSend(null);
            setTargetBranchId('');
            fetchAssets(selectedBranch.id, activeCategoryTab);
        } catch (error) {
            console.error("Error sending asset:", error);
            toast.error("Failed to send item");
        }
    };

    // Details Modal Logic
    const handleStatusChange = (e, isEditMode) => {
        const newStatus = e.target.value;
        if (isEditMode) {
            setEditFormData(prev => ({ ...prev, status: newStatus }));
            if (newStatus === 'For Repair' || newStatus === 'Not Functional') {
                setDetailsContext({ type: 'edit', status: newStatus, details: editFormData.details || '' });
                setIsDetailsModalOpen(true);
            }
        } else {
            setNewAssetData(prev => ({ ...prev, status: newStatus }));
            if (newStatus === 'For Repair' || newStatus === 'Not Functional') {
                setDetailsContext({ type: 'new', status: newStatus, details: newAssetData.details || '' });
                setIsDetailsModalOpen(true);
            }
        }
    };

    const handleSaveDetails = () => {
        if (detailsContext.type === 'edit') {
            setEditFormData(prev => ({ ...prev, details: detailsContext.details }));
        } else {
            setNewAssetData(prev => ({ ...prev, details: detailsContext.details }));
        }
        setIsDetailsModalOpen(false);
    };

    const openDetailsView = (asset) => {
        setDetailsContext({ type: 'view', status: asset.status, details: asset.details || '', assetId: asset.id });
        setIsDetailsModalOpen(true);
    };

    const handleSaveViewDetails = async () => {
        try {
            const updatedAsset = assets.find(a => a.id === detailsContext.assetId);
            await axiosInstance.put(`/inventory/assets/items/${detailsContext.assetId}`, {
                ...updatedAsset,
                details: detailsContext.details
            });
            toast.success("Details updated successfully");
            setIsDetailsModalOpen(false);
            fetchAssets(selectedBranch.id, activeCategoryTab);
        } catch (error) {
            console.error("Error updating details:", error);
            toast.error("Failed to update details");
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Functional': return 'text-green-600 font-medium';
            case 'For Repair': return 'text-orange-500 font-medium';
            case 'Not Functional': return 'text-red-600 font-medium';
            default: return 'text-gray-600';
        }
    };

    // Filter, Sort and Pagination Logic
    const filteredAssets = assets.filter(asset => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            (asset.item?.toLowerCase() || '').includes(query) ||
            (asset.description?.toLowerCase() || '').includes(query) ||
            (asset.inclusion?.toLowerCase() || '').includes(query) ||
            (asset.date_acquired?.toLowerCase() || '').includes(query) ||
            (asset.awarded_to?.toLowerCase() || '').includes(query) ||
            (asset.award_date?.toLowerCase() || '').includes(query) ||
            (asset.status?.toLowerCase() || '').includes(query)
        );
    });

    // Sort by Award Date (descending - most recent first)
    const sortedFilteredAssets = [...filteredAssets].sort((a, b) => {
        // Handle cases where award_date might be missing or empty
        const dateA = a.award_date ? new Date(a.award_date).getTime() : 0;
        const dateB = b.award_date ? new Date(b.award_date).getTime() : 0;
        return dateB - dateA;
    });

    const totalPages = Math.ceil(sortedFilteredAssets.length / itemsPerPage) || 1;
    const paginatedAssets = sortedFilteredAssets.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    return (
        <div className="flex h-full w-full">
            {/* Left Side - 30% */}
            <div className="w-[30%] border-r border-gray-200 dark:border-gray-800 p-4 flex flex-col gap-4 overflow-y-auto h-full bg-white dark:bg-black">
                <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold">Branch Offices</h2>
                </div>
                
                <div className="flex flex-col gap-2">
                    <Button 
                        onClick={() => setIsAddBranchOpen(true)} 
                        className="w-full flex items-center justify-center gap-2"
                    >
                        <Plus className="h-4 w-4" />
                        Add Branch Office
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => setSelectedBranch({ id: 'all', name: 'All Branches' })}
                        className={`w-full text-xs h-8 ${selectedBranch?.id === 'all' ? 'bg-gray-100 dark:bg-gray-800 font-medium' : ''}`}
                    >
                        Show All
                    </Button>
                </div>

                <div className="space-y-3">
                    {branches.map((branch) => (
                        <div 
                            key={branch.id} 
                            onClick={() => setSelectedBranch(branch)}
                            className={`border p-4 rounded-lg transition-colors cursor-pointer shadow-sm relative group ${
                                selectedBranch?.id === branch.id 
                                    ? 'bg-orange-50 border-orange-400 dark:bg-orange-950/30' 
                                    : 'border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900 bg-white dark:bg-gray-950'
                            }`}
                        >
                            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                                    onClick={(e) => openEditBranch(branch, e)}
                                    title="Edit Branch"
                                >
                                    <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                                    onClick={(e) => openDeleteBranch(branch, e)}
                                    title="Delete Branch"
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                            <div className="flex items-start justify-between">
                                <div className="flex items-center gap-2 font-semibold text-lg">
                                    <Building className={`h-5 w-5 ${selectedBranch?.id === branch.id ? 'text-orange-500' : 'text-gray-500'}`} />
                                    {branch.name}
                                </div>
                            </div>
                            <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 space-y-1">
                                <div className="flex justify-between">
                                    <span className="font-medium">PIC:</span>
                                    <span>{branch.person_in_charge}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-medium">Contact:</span>
                                    <span>{branch.contact_number}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                    {branches.length === 0 && (
                        <div className="text-center text-gray-500 py-8 italic">
                            No branch offices found. Add one to get started.
                        </div>
                    )}
                </div>
            </div>

            {/* Right Side - 70% */}
            <div className="w-[70%] bg-gray-50 dark:bg-gray-900 h-full overflow-hidden flex flex-col">
                {selectedBranch ? (
                    <div className="p-6 flex flex-col h-full">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                    {selectedBranch.name}
                                    <span className="text-gray-400 text-lg font-normal">Asset Categories</span>
                                </h2>
                                <p className="text-sm text-gray-500 mt-1">Manage acquired assets for this branch</p>
                            </div>
                            {selectedBranch.id !== 'all' && (
                                <Button onClick={handleAddCategoryClick} className="flex items-center gap-2">
                                    <Plus className="h-4 w-4" />
                                    Add Category
                                </Button>
                            )}
                        </div>

                        {/* Center Tab Menu */}
                        {categories.length > 0 && (
                            <div className="flex justify-center space-x-2 mb-4 overflow-x-auto pb-2 border-b border-gray-200 dark:border-gray-800 pt-8">
                                {categories.map(cat => (
                                    <div className="relative group flex flex-col items-center" key={cat.id}>
                                        {/* Hover Pop-up Actions */}
                                        {selectedBranch.id !== 'all' && (
                                            <div className="absolute -top-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex gap-1 bg-white dark:bg-gray-800 shadow-md border border-gray-200 dark:border-gray-700 rounded-md p-1 z-10 pointer-events-none group-hover:pointer-events-auto" style={{ transitionDelay: '2000ms' }}>
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon" 
                                                    className="h-6 w-6 text-blue-500 hover:text-blue-700 hover:bg-blue-50" 
                                                    onClick={(e) => { e.stopPropagation(); handleEditCategoryClick(cat); }}
                                                    title="Edit Category"
                                                >
                                                    <Pencil className="h-3 w-3" />
                                                </Button>
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon" 
                                                    className="h-6 w-6 text-red-500 hover:text-red-700 hover:bg-red-50" 
                                                    onClick={(e) => { e.stopPropagation(); handleDeleteCategoryClick(cat); }}
                                                    title="Delete Category"
                                                >
                                                    <Trash2 className="h-3 w-3" />
                                                </Button>
                                            </div>
                                        )}
                                        <button 
                                            onClick={() => setActiveCategoryTab(cat.name)}
                                            className={`px-6 py-2.5 rounded-t-lg text-sm font-medium transition-all duration-300 whitespace-nowrap border-b-2 ${
                                                activeCategoryTab === cat.name 
                                                    ? 'bg-orange-50/50 text-orange-600 border-orange-500 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-500' 
                                                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50 border-transparent dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-300'
                                            }`}
                                        >
                                            {cat.name}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {activeCategoryTab ? (
                            <div className="flex-1 flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-300">
                                <div className="flex justify-between items-center mb-4">
                                    <div className="relative w-72">
                                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                                        <Input
                                            placeholder="Search all columns..."
                                            className="pl-9 h-9 text-sm bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 focus:border-orange-500 focus:ring-orange-500"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className="text-sm font-medium text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 px-3 py-1.5 rounded-md border border-gray-200 dark:border-gray-700">
                                            Total: <span className="text-orange-600 dark:text-orange-400 ml-1">{assets.length}</span>
                                        </div>
                                        {selectedBranch.id !== 'all' && (
                                            <Button onClick={handleAddItemClick} variant="outline" className="flex items-center gap-2">
                                                <Plus className="h-4 w-4" />
                                                Add Item to {activeCategoryTab}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                <div className="bg-white dark:bg-black rounded-lg border border-gray-200 dark:border-gray-800 shadow-sm flex-1 overflow-auto flex flex-col">
                                    <div className="flex-1 overflow-auto">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                <TableHead className="w-[15%]">Item</TableHead>
                                                {selectedBranch.id === 'all' && (
                                                    <TableHead className="w-[10%]">Branch</TableHead>
                                                )}
                                                <TableHead className="w-[15%]">Description</TableHead>
                                                <TableHead className="w-[15%]">Inclusion</TableHead>
                                                <TableHead className="w-[10%]">Date Acquired</TableHead>
                                                <TableHead className="w-[15%]">Awarded To</TableHead>
                                                <TableHead className="w-[10%]">Award Date</TableHead>
                                                <TableHead className="w-[10%]">Status</TableHead>
                                                <TableHead className="w-[10%] text-right">Action</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {/* New Item Row (Input Mode) */}
                                            {isAddingItem && (
                                                <TableRow className="bg-orange-50/50 dark:bg-orange-950/10">
                                                    <TableCell>
                                                        <Input value={newAssetData.item} onChange={(e) => setNewAssetData({...newAssetData, item: e.target.value})} placeholder="Item" className="h-8 text-xs" autoFocus />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Input value={newAssetData.description} onChange={(e) => setNewAssetData({...newAssetData, description: e.target.value})} placeholder="Desc" className="h-8 text-xs" />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Input value={newAssetData.inclusion} onChange={(e) => setNewAssetData({...newAssetData, inclusion: e.target.value})} placeholder="Inclusion" className="h-8 text-xs" />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Input type="date" value={newAssetData.date_acquired} onChange={(e) => setNewAssetData({...newAssetData, date_acquired: e.target.value})} className="h-8 text-xs" />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Input value={newAssetData.awarded_to} onChange={(e) => setNewAssetData({...newAssetData, awarded_to: e.target.value})} placeholder="Awarded To" className="h-8 text-xs" />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Input type="date" value={newAssetData.award_date} onChange={(e) => setNewAssetData({...newAssetData, award_date: e.target.value})} className="h-8 text-xs" />
                                                    </TableCell>
                                                    <TableCell>
                                                        <select 
                                                            value={newAssetData.status} 
                                                            onChange={(e) => handleStatusChange(e, false)}
                                                            className={`h-8 w-full rounded-md border border-gray-300 bg-transparent px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-orange-500 ${getStatusColor(newAssetData.status)}`}
                                                        >
                                                            <option value="Functional" className="text-green-600">Functional</option>
                                                            <option value="For Repair" className="text-orange-500">For Repair</option>
                                                            <option value="Not Functional" className="text-red-600">Not Functional</option>
                                                        </select>
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <div className="flex justify-end gap-1">
                                                            <Button size="icon" variant="ghost" className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50" onClick={handleSaveNewItem}>
                                                                <Check className="h-4 w-4" />
                                                            </Button>
                                                            <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={handleCancelAddItem}>
                                                                <X className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            )}

                                            {/* Asset List */}
                                            {paginatedAssets.map((asset) => (
                                                <TableRow key={asset.id}>
                                                    {editingAssetId === asset.id ? (
                                                        // Edit Mode
                                                        <>
                                                            <TableCell>
                                                                <Input value={editFormData.item} onChange={(e) => setEditFormData({...editFormData, item: e.target.value})} className="h-8 text-xs" />
                                                            </TableCell>
                                                            {selectedBranch.id === 'all' && (
                                                                <TableCell className="text-xs text-gray-500">
                                                                    {getBranchName(asset.branch_id)}
                                                                </TableCell>
                                                            )}
                                                            <TableCell>
                                                                <Input value={editFormData.description} onChange={(e) => setEditFormData({...editFormData, description: e.target.value})} className="h-8 text-xs" />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Input value={editFormData.inclusion} onChange={(e) => setEditFormData({...editFormData, inclusion: e.target.value})} className="h-8 text-xs" />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Input type="date" value={editFormData.date_acquired} onChange={(e) => setEditFormData({...editFormData, date_acquired: e.target.value})} className="h-8 text-xs" />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Input value={editFormData.awarded_to} onChange={(e) => setEditFormData({...editFormData, awarded_to: e.target.value})} className="h-8 text-xs" />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Input type="date" value={editFormData.award_date} onChange={(e) => setEditFormData({...editFormData, award_date: e.target.value})} className="h-8 text-xs" />
                                                            </TableCell>
                                                            <TableCell>
                                                                <select 
                                                                    value={editFormData.status} 
                                                                    onChange={(e) => handleStatusChange(e, true)}
                                                                    className={`h-8 w-full rounded-md border border-gray-300 bg-transparent px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-orange-500 ${getStatusColor(editFormData.status)}`}
                                                                >
                                                                    <option value="Functional" className="text-green-600">Functional</option>
                                                                    <option value="For Repair" className="text-orange-500">For Repair</option>
                                                                    <option value="Not Functional" className="text-red-600">Not Functional</option>
                                                                </select>
                                                            </TableCell>
                                                            <TableCell className="text-right">
                                                                <div className="flex justify-end gap-1">
                                                                    <Button size="icon" variant="ghost" className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50" onClick={handleSaveEdit}>
                                                                        <Check className="h-4 w-4" />
                                                                    </Button>
                                                                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={handleCancelEdit}>
                                                                        <X className="h-4 w-4" />
                                                                    </Button>
                                                                </div>
                                                            </TableCell>
                                                        </>
                                                    ) : (
                                                        // View Mode
                                                        <>
                                                            <TableCell className="font-medium text-xs">{asset.item}</TableCell>
                                                            {selectedBranch.id === 'all' && (
                                                                <TableCell className="text-xs text-gray-500">
                                                                    {getBranchName(asset.branch_id)}
                                                                </TableCell>
                                                            )}
                                                            <TableCell className="text-xs text-gray-600">{asset.description}</TableCell>
                                                            <TableCell className="text-xs text-gray-600">{asset.inclusion}</TableCell>
                                                            <TableCell className="text-xs">{asset.date_acquired}</TableCell>
                                                            <TableCell className="text-xs">{asset.awarded_to}</TableCell>
                                                            <TableCell className="text-xs">{asset.award_date}</TableCell>
                                                            <TableCell className="text-xs">
                                                                <div className="flex items-center gap-2">
                                                                    <span className={getStatusColor(asset.status)}>{asset.status}</span>
                                                                    {(asset.status === 'For Repair' || asset.status === 'Not Functional') && (
                                                                        <button 
                                                                            onClick={() => openDetailsView(asset)}
                                                                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                                                            title="View Details"
                                                                        >
                                                                            <FileText className="h-3.5 w-3.5" />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="text-right">
                                                                <div className="flex justify-end gap-1">
                                                                    <Button size="icon" variant="ghost" className="h-8 w-8 text-gray-600 hover:text-gray-700 hover:bg-gray-100" onClick={() => handleHistoryClick(asset)} title="View Edit History">
                                                                        <History className="h-3.5 w-3.5" />
                                                                    </Button>
                                                                    <Button size="icon" variant="ghost" className="h-8 w-8 text-orange-600 hover:text-orange-700 hover:bg-orange-50" onClick={() => handleSendClick(asset)} title="Send Asset">
                                                                        <Send className="h-3.5 w-3.5" />
                                                                    </Button>
                                                                    <Button size="icon" variant="ghost" className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50" onClick={() => handleEditClick(asset)}>
                                                                        <Pencil className="h-3.5 w-3.5" />
                                                                    </Button>
                                                                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteAsset(asset.id, asset.branch_id)}>
                                                                        <Trash2 className="h-3.5 w-3.5" />
                                                                    </Button>
                                                                </div>
                                                            </TableCell>
                                                        </>
                                                    )}
                                                </TableRow>
                                            ))}

                                            {!isAddingItem && paginatedAssets.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={selectedBranch.id === 'all' ? 9 : 8} className="text-center py-8 text-gray-500 italic">
                                                        {searchQuery ? "No matching items found." : 'No items found in this category. Click "Add Item" to create one.'}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                    </div>
                                    {totalPages > 1 && (
                                        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
                                            <div className="text-sm text-gray-500">
                                                Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, sortedFilteredAssets.length)} of {sortedFilteredAssets.length} entries
                                            </div>
                                            <div className="flex gap-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                    disabled={currentPage === 1}
                                                    className="h-8"
                                                >
                                                    <ChevronLeft className="h-4 w-4 mr-1" />
                                                    Previous
                                                </Button>
                                                <div className="flex items-center justify-center text-sm font-medium px-2">
                                                    Page {currentPage} of {totalPages}
                                                </div>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                    disabled={currentPage === totalPages}
                                                    className="h-8"
                                                >
                                                    Next
                                                    <ChevronRight className="h-4 w-4 ml-1" />
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center text-gray-400 p-8 h-full flex-1">
                                <p className="text-lg">Select or create a category to manage items</p>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center text-gray-400 p-8 h-full">
                        <Building className="h-16 w-16 mb-4 opacity-20" />
                        <p className="text-lg">Select a branch office to view asset list</p>
                    </div>
                )}
            </div>

            {/* Add Category Modal */}
            <Dialog open={isAddCategoryModalOpen} onOpenChange={setIsAddCategoryModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add New Category</DialogTitle>
                        <DialogDescription>
                            Enter the name for the new asset category.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="category-name">Category Name</Label>
                            <Input 
                                id="category-name" 
                                value={newCategoryName}
                                onChange={(e) => setNewCategoryName(e.target.value)}
                                placeholder="e.g. Electronics, Furniture"
                                autoFocus
                            />
                        </div>
                    </div>
                    <DialogFooter className="mt-4">
                        <Button type="button" variant="outline" onClick={() => setIsAddCategoryModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleSaveNewCategory}>Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Details Modal (Repair / Damage) */}
            <Dialog open={isDetailsModalOpen} onOpenChange={setIsDetailsModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {detailsContext?.status === 'For Repair' ? 'Repair Details' : 'Damage Details'}
                        </DialogTitle>
                        <DialogDescription>
                            Please provide details regarding the item's condition. (Max 1000 characters)
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-2">
                        <Textarea 
                            value={detailsContext?.details || ''}
                            onChange={(e) => setDetailsContext({...detailsContext, details: e.target.value})}
                            placeholder="Enter details here..."
                            maxLength={1000}
                            className="h-32 resize-none"
                            readOnly={detailsContext?.type === 'view' && !editingAssetId}
                        />
                        <div className="text-right text-xs text-gray-500">
                            {(detailsContext?.details || '').length} / 1000
                        </div>
                    </div>
                    <DialogFooter className="mt-4">
                        {detailsContext?.type === 'view' && !editingAssetId ? (
                            <>
                                <Button type="button" variant="outline" onClick={() => setIsDetailsModalOpen(false)}>Close</Button>
                                <Button onClick={handleSaveViewDetails}>Update Details</Button>
                            </>
                        ) : (
                            <>
                                <Button type="button" variant="outline" onClick={() => setIsDetailsModalOpen(false)}>Cancel</Button>
                                <Button onClick={handleSaveDetails}>Save</Button>
                            </>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Category Modal */}
            <Dialog open={isEditCategoryModalOpen} onOpenChange={setIsEditCategoryModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit Category</DialogTitle>
                        <DialogDescription>
                            Update the name of the asset category.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="edit-category-name">Category Name</Label>
                            <Input 
                                id="edit-category-name" 
                                value={editCategoryName}
                                onChange={(e) => setEditCategoryName(e.target.value)}
                                placeholder="e.g. Electronics, Furniture"
                                autoFocus
                            />
                        </div>
                    </div>
                    <DialogFooter className="mt-4">
                        <Button type="button" variant="outline" onClick={() => setIsEditCategoryModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleSaveEditCategory}>Update</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Category Confirmation Modal */}
            <Dialog open={isDeleteCategoryModalOpen} onOpenChange={setIsDeleteCategoryModalOpen}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle className="text-red-600">Delete Category</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete the <strong>{categoryToDelete?.name}</strong> category? This action will permanently remove this category and ALL items within it. This cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-6 gap-2 sm:gap-0">
                        <Button type="button" variant="outline" onClick={() => setIsDeleteCategoryModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button 
                            type="button" 
                            variant="destructive" 
                            className="bg-red-600 hover:bg-red-700" 
                            onClick={handleConfirmDeleteCategory}
                        >
                            Delete Category
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Branch Modal */}
            <Dialog open={isAddBranchOpen} onOpenChange={setIsAddBranchOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add New Branch Office</DialogTitle>
                        <DialogDescription>
                            Enter the details for the new branch office.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateBranch} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="name">Branch Name</Label>
                            <Input 
                                id="name" 
                                value={formData.name}
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                placeholder="e.g. Makati Branch"
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="pic">Person In Charge (PIC)</Label>
                            <Input 
                                id="pic" 
                                value={formData.person_in_charge}
                                onChange={(e) => setFormData({...formData, person_in_charge: e.target.value})}
                                placeholder="Person In Charge"
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="contact">Contact Number</Label>
                            <Input 
                                id="contact" 
                                value={formData.contact_number}
                                onChange={(e) => setFormData({...formData, contact_number: e.target.value})}
                                placeholder="e.g. +63 912 345 6789"
                                required
                            />
                        </div>
                        <DialogFooter className="mt-4">
                            <Button type="button" variant="outline" onClick={() => setIsAddBranchOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="submit">Save</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
            {/* Edit Branch Modal */}
            <Dialog open={isEditBranchOpen} onOpenChange={setIsEditBranchOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit Branch Office</DialogTitle>
                        <DialogDescription>
                            Update the details for the branch office.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleEditBranch} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="edit-name">Branch Name</Label>
                            <Input 
                                id="edit-name" 
                                value={formData.name}
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                placeholder="e.g. Makati Branch"
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="edit-pic">Person In Charge (PIC)</Label>
                            <Input 
                                id="edit-pic" 
                                value={formData.person_in_charge}
                                onChange={(e) => setFormData({...formData, person_in_charge: e.target.value})}
                                placeholder="Person In Charge"
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="edit-contact">Contact Number</Label>
                            <Input 
                                id="edit-contact" 
                                value={formData.contact_number}
                                onChange={(e) => setFormData({...formData, contact_number: e.target.value})}
                                placeholder="e.g. +63 912 345 6789"
                                required
                            />
                        </div>
                        <DialogFooter className="mt-4">
                            <Button type="button" variant="outline" onClick={() => setIsEditBranchOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="submit">Update</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Branch Confirmation Modal */}
            <Dialog open={isDeleteBranchOpen} onOpenChange={setIsDeleteBranchOpen}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle className="text-red-600">Delete Branch Office</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete <strong>{branchToDelete?.name}</strong>? This action will permanently remove this branch and all its associated categories and assets. This cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-6 gap-2 sm:gap-0">
                        <Button type="button" variant="outline" onClick={() => setIsDeleteBranchOpen(false)}>
                            Cancel
                        </Button>
                        <Button 
                            type="button" 
                            variant="destructive" 
                            className="bg-red-600 hover:bg-red-700" 
                            onClick={handleDeleteBranch}
                        >
                            Delete Branch
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit History Modal */}
            <Dialog open={isHistoryModalOpen} onOpenChange={setIsHistoryModalOpen}>
                <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle>Edit History - {selectedAssetItemName}</DialogTitle>
                        <DialogDescription>
                            Track changes made to this asset.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex-1 overflow-y-auto pr-2 min-h-0">
                        {selectedAssetHistory.length === 0 ? (
                            <div className="text-center text-gray-500 py-8">No edit history available.</div>
                        ) : (
                            <div className="space-y-4">
                                {[...selectedAssetHistory].reverse().map((entry, idx) => (
                                    <div key={idx} className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-800">
                                        <div className="flex justify-between items-start mb-2">
                                            <span className="font-semibold text-sm">{entry.edited_by}</span>
                                            <span className="text-xs text-gray-500">
                                                {new Date(entry.edited_at).toLocaleString()}
                                            </span>
                                        </div>
                                        <div className="space-y-1 mt-2">
                                            {entry.changes.map((change, cIdx) => (
                                                <div key={cIdx} className="text-xs flex flex-col gap-1 bg-white dark:bg-black p-2 rounded border border-gray-100 dark:border-gray-800">
                                                    <span className="font-medium text-gray-700 dark:text-gray-300 capitalize">{change.field.replace('_', ' ')}</span>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className="text-gray-500 truncate" title={change.field === 'branch_id' ? getBranchName(change.old_value) : (change.old_value || 'None')}>
                                                            {change.field === 'branch_id' ? getBranchName(change.old_value) : (change.old_value || 'None')}
                                                        </div>
                                                        <div className="text-green-600 truncate" title={change.field === 'branch_id' ? getBranchName(change.new_value) : (change.new_value || 'None')}>
                                                            {change.field === 'branch_id' ? getBranchName(change.new_value) : (change.new_value || 'None')}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => setIsHistoryModalOpen(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Send Asset Modal */}
            <Dialog open={isSendAssetModalOpen} onOpenChange={setIsSendAssetModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Send Asset</DialogTitle>
                        <DialogDescription>
                            Select a branch to send the item <strong>{assetToSend?.item}</strong>.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="target-branch">Target Branch</Label>
                            <select 
                                id="target-branch"
                                value={targetBranchId}
                                onChange={(e) => setTargetBranchId(e.target.value)}
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <option value="" disabled>Select a branch</option>
                                {branches.filter(b => b.id !== assetToSend?.branch_id).map(branch => (
                                    <option key={branch.id} value={branch.id}>
                                        {branch.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setIsSendAssetModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleSendAsset}>Send</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AcquiredAssets;
